import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, getPaginated, post } from '@/lib/api'

import type { PayslipStatus } from '@company/shared'


// ==================== Types ====================

export type { PayslipStatus }

export interface Salary {
  id: string
  employeeId: string
  baseSalary: number
  allowance: number | null
  effectiveDate: string
  remark: string | null
  createdAt: string
  createdBy: string | null
}

export interface SalaryWithEmployee extends Salary {
  employee?: {
    id: string
    name: string
    employeeNo: string
  } | null
}

export interface Payslip {
  id: string
  employeeId: string
  month: string
  baseSalary: number
  allowance: number | null
  deduction: number | null
  netSalary: number
  unpaidLeaveDays: number | null
  status: PayslipStatus
  approvedBy: string | null
  approvedAt: string | null
  paidAt: string | null
  remark: string | null
  createdAt: string
  updatedAt: string | null
}

export interface PayslipWithEmployee extends Payslip {
  employee?: {
    id: string
    name: string
    employeeNo: string
  } | null
  approver?: {
    id: string
    name: string
  } | null
}

export interface SalaryListParams {
  employeeId?: string
  effectiveDateFrom?: string
  effectiveDateTo?: string
  page?: number
  pageSize?: number
}

export interface PayslipListParams {
  employeeId?: string
  month?: string
  status?: PayslipStatus
  page?: number
  pageSize?: number
}

export interface SetSalaryInput {
  employeeId: string
  baseSalary: number
  allowance?: number
  effectiveDate: string
  remark?: string
}

// ==================== Query Keys ====================

export const salaryKeys = {
  all: ['salaries'] as const,
  lists: () => [...salaryKeys.all, 'list'] as const,
  list: (params: SalaryListParams) => [...salaryKeys.lists(), params] as const,
  history: (employeeId: string) => [...salaryKeys.all, 'history', employeeId] as const,
  payslips: () => [...salaryKeys.all, 'payslips'] as const,
  payslipList: (params: PayslipListParams) => [...salaryKeys.payslips(), 'list', params] as const,
  payslipDetail: (id: string) => [...salaryKeys.payslips(), 'detail', id] as const,
}

// ==================== Hooks ====================

/**
 * 获取薪资设置列表
 */
export function useSalaries(params: SalaryListParams = {}) {
  return useQuery({
    queryKey: salaryKeys.list(params),
    queryFn: () =>
      getPaginated<SalaryWithEmployee>('/hr/salaries', params as Record<string, unknown>),
  })
}

/**
 * 获取员工薪资历史
 */
export function useSalaryHistory(employeeId: string) {
  return useQuery({
    queryKey: salaryKeys.history(employeeId),
    queryFn: () => get<Salary[]>(`/hr/salaries/${employeeId}`),
    enabled: !!employeeId,
  })
}

/**
 * 设置薪资
 */
export function useSetSalary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SetSalaryInput) => post<Salary>('/hr/salaries', data),
    onSuccess: (_, { employeeId }) => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: salaryKeys.history(employeeId) })
    },
  })
}

/**
 * 获取薪资单列表
 */
export function usePayslips(params: PayslipListParams = {}) {
  return useQuery({
    queryKey: salaryKeys.payslipList(params),
    queryFn: () =>
      getPaginated<PayslipWithEmployee>('/hr/salaries/payslips', params as Record<string, unknown>),
  })
}

/**
 * 获取薪资单详情
 */
export function usePayslip(id: string) {
  return useQuery({
    queryKey: salaryKeys.payslipDetail(id),
    queryFn: () => get<PayslipWithEmployee>(`/hr/salaries/payslips/${id}`),
    enabled: !!id,
  })
}

/**
 * 生成薪资单
 */
export function useGeneratePayslips() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (month: string) => post<Payslip[]>('/hr/salaries/payslips/generate', { month }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslips() })
    },
  })
}

/**
 * 审批薪资单
 */
export function useApprovePayslip() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Payslip>(`/hr/salaries/payslips/${id}/approve`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslipDetail(id) })
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslips() })
    },
  })
}

/**
 * 标记已发放
 */
export function useMarkPayslipAsPaid() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<Payslip>(`/hr/salaries/payslips/${id}/pay`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslipDetail(id) })
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslips() })
    },
  })
}

/**
 * 批量审批
 */
export function useBatchApprovePayslips() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => post<Payslip[]>('/hr/salaries/payslips/batch-approve', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslips() })
    },
  })
}

/**
 * 批量标记已发放
 */
export function useBatchMarkPayslipsAsPaid() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => post<Payslip[]>('/hr/salaries/payslips/batch-pay', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: salaryKeys.payslips() })
    },
  })
}
